// Contrats d'échange entre l'API et le front.

import { z } from 'zod';

export const APP_HEADER = 'cockpit';

export const RoleSchema = z.enum(['admin', 'direction', 'da']);
export type Role = z.infer<typeof RoleSchema>;

export const MeSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  /** Entités SYNC visibles ; vide = toutes. */
  entities: z.array(z.string()),
  provider: z.enum(['entra', 'local']),
  /** `cloudron` : accès protégé par l'authentification Cloudron (proxyAuth), sans connexion propre à l'application. */
  authMode: z.enum(['app', 'cloudron']),
});
export type Me = z.infer<typeof MeSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

/** Nom affiché dans l'application (« traité par », menu du compte). */
export const ProfileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Le nom doit compter au moins 2 caractères.')
    .max(80, 'Le nom ne peut pas dépasser 80 caractères.'),
});

export const PasswordChangeSchema = z.object({
  current: z.string().min(1, 'Saisissez votre mot de passe actuel.'),
  next: z.string().min(1, 'Saisissez le nouveau mot de passe.').max(200),
});

export const TraitementSchema = z.object({
  done: z.boolean(),
  note: z.string().max(500).optional(),
});

export interface TraitementDto {
  code: string;
  by: string;
  at: string;
  note: string | null;
}

export const ImportKindSchema = z.enum(['cockpit-xlsx', 'cockpit-json', 'plan-json']);
export type ImportKind = z.infer<typeof ImportKindSchema>;

export interface ImportRunDto {
  id: string;
  kind: string;
  fileName: string;
  status: 'pending' | 'running' | 'ok' | 'error';
  rows: number | null;
  message: string | null;
  by: string;
  createdAt: string;
  finishedAt: string | null;
}

export interface SyncRunDto {
  id: string;
  job: string;
  status: 'running' | 'ok' | 'error';
  rows: number | null;
  message: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface HealthDto {
  status: 'ok' | 'degraded' | 'down';
  version: string;
  uptimeSeconds: number;
  checks: Record<string, 'ok' | 'down' | 'stale' | 'absent'>;
}

export const UserUpdateSchema = z.object({
  role: RoleSchema,
  entities: z.array(z.string()),
});

export interface UserDto {
  id: string;
  email: string;
  name: string;
  role: Role;
  entities: string[];
  provider: 'entra' | 'local';
  lastLoginAt: string | null;
  /** Compte administrateur local indépendant du SSO : il ne peut pas être supprimé. */
  protected: boolean;
}
